import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { BreadcrumbSchema } from "@/components/StructuredData";
import { Card, CardContent } from "@/components/ui/card";
import { Heart, Users, Palette, Camera } from "lucide-react";

const About = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="About SKECHA - Our Story & Mission"
        description="Learn about SKECHA's mission to bring families together through creativity. Transform digital memories into personalized coloring adventures for kids and families."
        keywords="about skecha, coloring page creator, family creativity, children activities, photo transformation story"
        url="https://skecha.com/about"
      />
      <BreadcrumbSchema
        items={[
          { name: "Home", url: "https://skecha.com" },
          { name: "About", url: "https://skecha.com/about" },
        ]}
      />
      <Header />
      
      <main className="container mx-auto px-4 py-16">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-6xl font-bold bg-gradient-hero bg-clip-text text-transparent mb-6">
            About SKECHA
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Born from a love of creativity and family connections, SKECHA transforms your digital memories 
            into personalized coloring adventures that bring families together.
          </p>
        </div>

        {/* Our Story */}
        <div className="mb-16">
          <Card className="bg-gradient-to-br from-creative-purple/5 to-creative-coral/5 border-creative-purple/20">
            <CardContent className="p-8 md:p-12">
              <div className="flex items-center gap-3 mb-6">
                <Heart className="w-8 h-8 text-creative-coral" />
                <h2 className="text-3xl font-bold">Our Story</h2>
              </div>
              <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                SKECHA was created out of pure love for creativity in kids and families. We believe that 
                every photo tells a story, and every story deserves to be brought to life through art and imagination.
              </p>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Our mission is simple: to give families a new way of interacting with their digital memories, 
                transforming treasured moments into creative experiences that spark joy, conversation, and connection.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Values Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          <Card className="text-center p-8 hover:shadow-elegant transition-shadow">
            <div className="w-16 h-16 bg-gradient-hero rounded-full flex items-center justify-center mx-auto mb-4">
              <Palette className="w-8 h-8 text-primary-foreground" />
            </div>
            <h3 className="text-xl font-semibold mb-3">Creativity First</h3>
            <p className="text-muted-foreground">
              We believe every child is an artist waiting to create something amazing. Our tools are designed 
              to inspire and nurture creative expression.
            </p>
          </Card>

          <Card className="text-center p-8 hover:shadow-elegant transition-shadow">
            <div className="w-16 h-16 bg-gradient-hero rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-primary-foreground" />
            </div>
            <h3 className="text-xl font-semibold mb-3">Family Connection</h3>
            <p className="text-muted-foreground">
              Technology should bring families together, not apart. We create experiences that encourage 
              quality time and meaningful interactions.
            </p>
          </Card>

          <Card className="text-center p-8 hover:shadow-elegant transition-shadow">
            <div className="w-16 h-16 bg-gradient-hero rounded-full flex items-center justify-center mx-auto mb-4">
              <Camera className="w-8 h-8 text-primary-foreground" />
            </div>
            <h3 className="text-xl font-semibold mb-3">Memory Magic</h3>
            <p className="text-muted-foreground">
              Your photos are precious memories. We transform them into interactive experiences that 
              make those moments even more special.
            </p>
          </Card>
        </div>

        {/* Team Section */}
        <div className="text-center">
          <Card className="bg-gradient-to-br from-creative-mint/5 to-creative-lavender/5 border-creative-mint/20">
            <CardContent className="p-8 md:p-12">
              <h2 className="text-3xl font-bold mb-6">Made in Melbourne</h2>
              <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                Our passionate team is based in the vibrant creative hub of Melbourne, Australia. 
                We're parents, artists, and technologists who understand the importance of nurturing 
                creativity in the digital age.
              </p>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Every feature we build is tested with real families, ensuring that SKECHA delivers 
                not just technology, but genuine moments of joy and connection.
              </p>
            </CardContent>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default About;